import React, { useId, useState } from "react";
import PropTypes from "prop-types";
import { ChevronDown } from "lucide-react";
import BridgeText from "./BridgeText";
import { cn } from "../utils/classNames";
import { handleInternalLinkClick } from "../utils/navigation";
import { trackEvent } from "../utils/analytics";

function sentenceCase(value) {
  if (typeof value !== "string" || !value) return "";
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}

const DiscoveryCue = ({
  cue = null,
  currentSlug = "",
  currentTitle = "",
  surface,
  compact = false,
  className,
}) => {
  const [whyOpen, setWhyOpen] = useState(false);
  const whyId = useId();
  const anchor = cue?.type === "inbound" && cue.anchor?.slug && cue.anchor?.title
    ? cue.anchor
    : null;
  const tasteLabel = cue?.type === "taste" && typeof cue.label === "string"
    ? cue.label.trim()
    : "";
  if (!anchor && !tasteLabel) {
    return null;
  }

  const bridge = anchor && typeof cue.bridge === "string" ? cue.bridge.trim() : "";
  const reasons = !compact && cue?.type === "taste" && Array.isArray(cue.reasons)
    ? cue.reasons.filter((reason) => typeof reason === "string" && reason.trim()).slice(0, 2)
    : [];

  return (
    <div
      className={cn("space-y-1 border-t border-border/60 pt-2", className)}
      data-discovery-cue={anchor ? "inbound" : "taste"}
    >
      <p className="text-body-xs leading-relaxed text-muted-foreground">
        {anchor ? (
          <>
            <span>For fans of </span>
            <a
              href={`/show/${anchor.slug}`}
              className="font-semibold text-foreground underline-offset-2 hover:text-primary hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={`View ${anchor.title}, the recommendation reference for this show`}
              onClick={(event) => {
                event.stopPropagation();
                trackEvent("discovery_cue_click", {
                  surface,
                  cue_type: "inbound",
                  current_slug: currentSlug,
                  anchor_slug: anchor.slug,
                });
                handleInternalLinkClick(event, `/show/${anchor.slug}`);
              }}
            >
              {anchor.title}
            </a>
          </>
        ) : (
          <span className="font-semibold text-foreground">{sentenceCase(tasteLabel)}</span>
        )}
      </p>
      {bridge ? (
        <>
          <button
            type="button"
            aria-expanded={whyOpen}
            aria-controls={whyId}
            onClick={(event) => {
              event.stopPropagation();
              if (!whyOpen) trackEvent("discovery_cue_why_open", { surface, current_slug: currentSlug, anchor_slug: anchor.slug });
              setWhyOpen((open) => !open);
            }}
            className="flex min-h-8 items-center gap-1 rounded-sm text-body-xs font-semibold text-primary underline-offset-2 hover:underline"
          >
            Why it fits
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", whyOpen && "rotate-180")} aria-hidden="true" />
          </button>
          <p id={whyId} hidden={!whyOpen} className="text-body-xs leading-relaxed text-foreground/85">
            <BridgeText text={bridge} titles={[anchor.title, currentTitle].filter(Boolean)} />
          </p>
        </>
      ) : null}
      {reasons.length > 0 ? (
        <p className="line-clamp-1 text-body-xs text-muted-foreground">
          {reasons.map(sentenceCase).join(" · ")}
        </p>
      ) : null}
    </div>
  );
};

DiscoveryCue.propTypes = {
  cue: PropTypes.shape({
    type: PropTypes.oneOf(["inbound", "taste"]),
    label: PropTypes.string,
    anchor: PropTypes.shape({
      slug: PropTypes.string,
      title: PropTypes.string,
    }),
    bridge: PropTypes.string,
    reasons: PropTypes.arrayOf(PropTypes.string),
  }),
  currentSlug: PropTypes.string,
  currentTitle: PropTypes.string,
  surface: PropTypes.string.isRequired,
  compact: PropTypes.bool,
  className: PropTypes.string,
};

export default DiscoveryCue;
